export type AuthProviderHint = "easyauth" | "msal" | "unknown"

export type AuthErrorDetails = {
  id: string
  at: string
  reason: string
  message?: string
  provider?: AuthProviderHint
  returnTo?: string
  context?: Record<string, unknown>
}

const KEY = "auth_last_error_v1"

function generateId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID()
    }
  } catch {
    // ignore
  }
  return `autherr_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

export function storeAuthError(
  input: Omit<AuthErrorDetails, "id" | "at"> & Partial<Pick<AuthErrorDetails, "id" | "at">>
): AuthErrorDetails {
  const details: AuthErrorDetails = {
    id: input.id ?? generateId(),
    at: input.at ?? new Date().toISOString(),
    reason: input.reason,
    message: input.message,
    provider: input.provider ?? "unknown",
    returnTo: input.returnTo,
    context: input.context,
  }

  try {
    sessionStorage.setItem(KEY, JSON.stringify(details))
  } catch {
    // ignore (sessionStorage may be unavailable)
  }

  return details
}

export function readAuthError(): AuthErrorDetails | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return null

    const data = parsed as Record<string, unknown>
    const reason = data["reason"]
    if (typeof reason !== "string") return null

    const providerRaw = data["provider"]
    const provider: AuthProviderHint =
      providerRaw === "easyauth" || providerRaw === "msal" || providerRaw === "unknown"
        ? providerRaw
        : "unknown"

    const contextRaw = data["context"]
    const context =
      typeof contextRaw === "object" && contextRaw !== null
        ? (contextRaw as Record<string, unknown>)
        : undefined

    return {
      id: typeof data["id"] === "string" ? (data["id"] as string) : generateId(),
      at: typeof data["at"] === "string" ? (data["at"] as string) : new Date().toISOString(),
      reason,
      message: typeof data["message"] === "string" ? (data["message"] as string) : undefined,
      provider,
      returnTo: typeof data["returnTo"] === "string" ? (data["returnTo"] as string) : undefined,
      context,
    }
  } catch {
    return null
  }
}

export function clearAuthError(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}
